import React, { useEffect, useState } from 'react';
import { Save, Trash2, User, Plus, Settings as SettingsIcon } from 'lucide-react';
import { useStore } from '../store';
import type { TrackerUser } from '../types';
import { mqttClient } from '../lib/mqtt';
import { api } from '../lib/api';

export const UserPanel: React.FC = () => {
  const { users, setUsers, settings, updateSettings } = useStore();
  const [tempNames, setTempNames] = useState<{ [key: string]: string }>({});
  const [isComposing, setIsComposing] = useState(false);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const loadedUsers = await api.getUsers();
        if (Array.isArray(loadedUsers)) {
          setUsers(loadedUsers);
          const initialNames: { [key: string]: string } = {};
          loadedUsers.forEach(user => {
            initialNames[user.id] = user.name;
          });
          setTempNames(initialNames);
        }
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadUsers();
  }, [setUsers]);

  const handleUpdateUser = async (index: number, updates: Partial<TrackerUser>) => {
    const updatedUsers = [...users];
    if (!updatedUsers[index]) {
      updatedUsers[index] = {
        id: `user-${Date.now()}-${index}`,
        name: `ユーザー${index + 1}`,
        topic: 'owntracks/user/device',
        ...updates
      };
    } else {
      updatedUsers[index] = {
        ...updatedUsers[index],
        ...updates
      };
    }
    
    try {
      await api.saveUser(updatedUsers[index]);
      setUsers(updatedUsers);
      // ユーザー情報が更新されたらMQTTクライアントを再接続
      mqttClient.reconnect();
    } catch (error) {
      console.error('Error updating user:', error);
      alert('ユーザー情報の更新に失敗しました');
    }
  };

  const handleDeleteUser = async (index: number) => {
    if (window.confirm('このユーザーを削除してもよろしいですか？')) {
      try {
        await api.deleteUser(users[index].id);
        const updatedUsers = [...users];
        updatedUsers.splice(index, 1);
        setUsers(updatedUsers);
        // ユーザーが削除されたらMQTTクライアントを再接続
        mqttClient.reconnect();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('ユーザーの削除に失敗しました');
      }
    }
  };

  const handleAddUser = async () => {
    if (users.length >= 2) {
      alert('最大2人までのユーザーを登録できます');
      return;
    }

    const newUser: TrackerUser = {
      id: `user-${Date.now()}-${users.length}`,
      name: `ユーザー${users.length + 1}`,
      topic: 'owntracks/user/device'
    };

    try {
      await api.saveUser(newUser);
      const updatedUsers = [...users, newUser];
      setUsers(updatedUsers);
      setTempNames(prev => ({
        ...prev,
        [newUser.id]: newUser.name
      }));
      // 新しいユーザーが追加されたらMQTTクライアントを再接続
      mqttClient.reconnect();
    } catch (error) {
      console.error('Error adding user:', error);
      alert('ユーザーの追加に失敗しました');
    }
  };

  const handleSave = async () => {
    try {
      await Promise.all(users.map(user => api.saveUser(user)));
      // 保存後にMQTTクライアントを再接続
      mqttClient.reconnect();
      alert('設定を保存しました');
    } catch (error) {
      console.error('Error saving users:', error);
      alert('ユーザー情報の保存に失敗しました');
    }
  };

  const handleToleranceChange = (type: 'route' | 'checkpoint', value: string) => {
    const tolerance = parseInt(value, 10);
    if (!isNaN(tolerance) && tolerance > 0) {
      if (type === 'route') {
        updateSettings({ routeTolerance: tolerance });
      } else {
        updateSettings({ checkpointTolerance: tolerance });
      }
    }
  };

  const handleNameChange = (userId: string, value: string) => {
    setTempNames(prev => ({
      ...prev,
      [userId]: value
    }));
  };

  const handleNameBlur = async (index: number, userId: string) => {
    if (!isComposing && tempNames[userId] !== users[index].name) {
      await handleUpdateUser(index, { name: tempNames[userId] });
    }
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow-lg w-96">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold">ユーザー設定</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={handleAddUser}
            disabled={users.length >= 2}
            className="p-1 text-gray-600 hover:text-blue-500 disabled:text-gray-400"
            title={users.length >= 2 ? '最大2人まで登録できます' : 'ユーザーを追加'}
          >
            <Plus className="w-5 h-5" />
          </button>
          <User className="w-5 h-5 text-gray-600" />
        </div>
      </div>

      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <SettingsIcon className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold">システム設定</h3>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              ルートからの許容距離 (メートル)
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={settings.routeTolerance}
              onChange={(e) => handleToleranceChange('route', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500">
              ユーザーがルート上にいると判定される最大距離を設定します。
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              スタート/ゴール判定距離 (メートル)
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={settings.checkpointTolerance}
              onChange={(e) => handleToleranceChange('checkpoint', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500">
              スタートとゴールの判定に使用される距離を設定します。
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {users.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <p>ユーザーが登録されていません</p>
            <button
              onClick={handleAddUser}
              className="mt-2 text-blue-500 hover:text-blue-600"
            >
              ユーザーを追加
            </button>
          </div>
        ) : (
          users.map((user, index) => (
            <div key={user.id} className="p-4 bg-white border rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">ユーザー {index + 1}</h3>
                <button
                  onClick={() => handleDeleteUser(index)}
                  className="p-1 text-gray-600 hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    名前
                  </label>
                  <input
                    type="text"
                    value={tempNames[user.id] || ''}
                    onChange={(e) => handleNameChange(user.id, e.target.value)}
                    onCompositionStart={() => setIsComposing(true)}
                    onCompositionEnd={() => {
                      setIsComposing(false);
                      handleNameBlur(index, user.id);
                    }}
                    onBlur={() => handleNameBlur(index, user.id)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ユーザー名"
                    lang="ja"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Owntracks Topic
                  </label>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={user.topic}
                      onChange={(e) => handleUpdateUser(index, { topic: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="owntracks/user/device"
                      spellCheck="false"
                      autoComplete="off"
                      autoCapitalize="off"
                    />
                    <p className="text-xs text-gray-500">
                      例: owntracks/atu/spacewar
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
        {users.length > 0 && (
          <button
            onClick={handleSave}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            <Save className="w-4 h-4" />
            保存
          </button>
        )}
      </div>
    </div>
  );
};